import React, {CSSProperties, FC, memo} from 'react';
import {Media, MontanaMediaRatios} from "@montana/shared/media";
import {IStaticProductPage} from "@montana/models/generated/cms.interface";
import {getTextColorFor} from "@montana/helpers/theme-color.helper";
import {useTranslations} from "@montana/containers/translations";
import {ColorSwatch} from "@montana/shared/color-swatch";
import {Price} from "@montana/shared/price";
import {Button} from "@montana/shared/button";
import {Link} from "@montana/shared/link";
import {Breadcrumbs} from "@montana/shared/breadcrumbs";
import {Appear} from "@montana/shared/appear";
import {SeriesLink} from "@montana/shared/series-link/series-link.component";

const HERO_FIRST_APPEAR_DELAY_MS = 400;

export type HeroLayouts = 'landscape' | 'square' | 'no-image' | 'portrait' | 'wide';

interface IMediaSizes {
    'landscape'?: string;
    'square'?: string;
    'no-image'?: string;
    'portrait'?: string;
    'wide'?: string;
}

const mediaSizes: IMediaSizes = {
    portrait: [
        '(min-width: 1680px) 470px',
        '(min-width: 1367px) 411px',
        '(min-width: 811px) 30vw',
        '(min-width: 480px) 30vw',
        '80vw'
    ].join(', '),
    wide: '100vw',
}

export interface IHeroStaticProductModuleProps extends IStaticProductPage {
    backgroundColorHex?: string;
    isFirstRendering?: boolean;
}

export const HeroStaticProductModule: FC<IHeroStaticProductModuleProps> = memo((props: IHeroStaticProductModuleProps) => {
    const {
        backgroundColorHex = '#ffffff',
        isFirstRendering = true,
        productImage,
        productName,
        productDescription,
        productDesigner,
        productColors,
        specifications,
        seriesLink,
        usPs,
        link,
        fromPrice,
    } = props;

    const heroLayout: HeroLayouts = (productImage
        ? 'portrait'
        : 'no-image') as HeroLayouts;

    const style = {
        '--background-color': backgroundColorHex,
        '--swatch-color': getTextColorFor(backgroundColorHex),
    } as CSSProperties;

    const translate = useTranslations();

    return (
        <>
            <section className="static-product-hero-block" style={style}>
                <div className="static-product-hero-block__bg" style={{
                    animationDelay: isFirstRendering ? `${HERO_FIRST_APPEAR_DELAY_MS}ms` : ''
                }}></div>
                <div className="static-product-hero-block__content">
                    {productImage && (
                        <div className="static-product-hero-block__media-container">
                            <Media
                                image={productImage}
                                mediaRatio={MontanaMediaRatios.StaticPDP}
                                placeholder={heroLayout !== 'wide'}
                                animate={true}
                                sizes={mediaSizes[heroLayout]}
                                eager={true}
                            />
                        </div>
                    )}
                    <div className="static-product-hero-block__spacer"></div>
                    <div className="static-product-hero-block__info">
                        <section className="static-product-hero-block__product-text">
                            <h1>{productName}</h1>
                        {productDescription && <span dangerouslySetInnerHTML={{__html: productDescription}}></span>}
                        </section>
                        <section className="static-product-hero-block__product-details-container">
                            {seriesLink?.url && (<div className="static-product-hero-block__product-details">
                                <header>
                                    <span
                                        className="static-product-hero-block__product-details-title">{translate((t) => t.productPage.productPageSeries)}</span>
                                </header>
                                <SeriesLink link={seriesLink} title={seriesLink?.name || seriesLink?.name || ''} style={{display: 'inline-flex', width: 'fit-content', '--underline-offset': '1px'} as CSSProperties}/>
                            </div>)}
                            {!seriesLink?.url && productDesigner && (<div className="static-product-hero-block__product-details">
                                <header>
                                    <span
                                        className="static-product-hero-block__product-details-title">{translate((t) => t.productPage.productPageDesigner)}</span>
                                </header>
                                <p>{productDesigner}</p>
                            </div>)}
                            <div className="static-product-hero-block__product-details">
                                <header>
                                    <span
                                        className="static-product-hero-block__product-details-title">{translate(t => t.staticProductPage.staticProductPageColourSectionTitle)}</span>
                                </header>
                                <div className="static-product-hero-block__color-row">
                                    {productColors?.map((color) =>
                                    <Link key={color.colorId} to={color.link?.url}>
                                        <ColorSwatch variant="row" colorSwatchImage={color?.colorSwatchImage}/>
                                    </Link>
                                    )}
                                </div>
                            </div>
                            <div className="static-product-hero-block__product-details">
                                <header>
                                    <span
                                        className="static-product-hero-block__product-details-title">{translate((t) => t.productPage.productPageSpecifications)}</span>
                                </header>
                                <ul>
                                    {specifications?.map((spec) => (
                                        <li key={spec}>{spec}</li>
                                    ))}
                                </ul>
                            </div>
                        </section>
                        <section className="static-product-hero-block__buyer-info">
                            <div className="static-product-hero-block__price">
                                <span  className="static-product-hero-block__price-title">
                                    {translate(t => t.productPage.professionalsProductPageFrom)} <Price obj={fromPrice}/>
                                </span>
                                <p>{translate(t => t.staticProductPage.staticProductPriceCanChangeDescription)}</p>
                            </div>
                            <div className="static-product-hero-block__user-actions">
                                {link?.url && <Button style="tertiary" href={link?.url}>{link?.name}</Button>}
                            </div>
                            <div className="static-product-hero-block__usps">
                                {usPs?.map((usp) => (
                                    <span key={usp} className="static-product-hero-block__usp">{usp}</span>
                                ))}
                            </div>
                        </section>
                        <Appear delay={isFirstRendering ? HERO_FIRST_APPEAR_DELAY_MS : 0}
                                className="color-hero-block__breadcrumbs">
                            <Breadcrumbs />
                        </Appear>
                    </div>
                </div>
            </section>
        </>
    );
});
